import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { WAREHOUSE_KEYS } from '../lib';
import { WarehouseApi } from '../api';
import type {
  WarehouseBase,
  WarehouseById,
  WarehouseCreatePayload,
  WarehouseUpdatePayload,
  WarehouseItem,
  WarehouseItemListParams,
} from './types';

export const useWarehouseItemList = (params?: WarehouseItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasSearchFilter = Boolean(params?.search && params.search.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<WarehouseItem[]>, Error, WarehouseItem[] | undefined>({
    queryKey: WAREHOUSE_KEYS.itemList(params),
    queryFn: () => WarehouseApi.getItemList(params),
    enabled: hasNameFilter || hasSearchFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useWarehouseList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<WarehouseBase>>({
    queryKey: WAREHOUSE_KEYS.list(params),
    queryFn: () => WarehouseApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useWarehouseById = (id: number) => {
  return useQuery<ApiResponse<WarehouseById>>({
    queryKey: WAREHOUSE_KEYS.detail(id),
    queryFn: () => WarehouseApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateWarehouse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WarehouseCreatePayload) => WarehouseApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WAREHOUSE_KEYS.all });
    },
  });
};

export const useUpdateWarehouse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WarehouseUpdatePayload) => WarehouseApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WAREHOUSE_KEYS.all });
    },
  });
};

export const useDeleteWarehouse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => WarehouseApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WAREHOUSE_KEYS.all });
    },
  });
};
